import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Box,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { PieChart, pieArcLabelClasses } from '@mui/x-charts/PieChart';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ReportGraphLegend from './reportGraphLegend';

import NoDataFound from '~/components/common/noDataFound';
import { ROLE_VALUES } from '~/constants/customerConstant';
import {
  DEFAULT_VALUE,
  INITIAL_VALUE,
  usePartnerContext,
} from '~/contexts/partner/partnerContext';
import { WHISTLEBLOWING_SPECIFIC_METRICS } from '~/features/intake-channels/constants';
import { useDashoardFilter } from '~/Page/customer/customerDashboard';
import {
  MetricsService,
  PieChartMetricTypes,
} from '~/services/api/metrics/endpoints';
import QueryResolver from '~/services/api/query-resolver';
import { useAuthSlice } from '~/slices/auth';
import { useIntakeChannel } from '~/slices/intakeChannelSlice';

interface ReportFilterProps {
  filterBy: string;
  date: string;
  dateEnd: string;
  tenantId: number;
}

interface PieChartObject {
  value: number;
  label: string;
}

interface PieChartProps {
  filter: ReportFilterProps;
  option: PieChartMetricTypes;
}

interface ReportsPieChartProps {
  ReportsDataList: PieChartObject[];
  report: string;
}

enum ReportFilters {
  SEVERITY = 'Severity',
  CONFIDENTIALITY = 'Confidentiality',
  AGE_OF_REPORT = 'Age of Report',
  FINDINGS = 'Findings',
}

const severityPalette = ['#99AAFF', '#738BFF', '#506EFF', '#2449FC'];
const confidentialityPalette = ['#506EFF', '#2449FC'];

const headerText = {
  fontSize: '18px',
  fontWeight: 500,
};

const getPercentage = (list: PieChartObject[], current: number) => {
  let total = 0;
  list.map((item) => (total += item.value));

  return ((current / total) * 100).toFixed(0);
};

// CHECK IF CHANNEL ID IS NOT WHISTLEBLOWING (1) AND RETURN ALL WHISTLEBLOWING SPECIFIC METRICS TO BE DISABLED IN DASHBOARD
export const getDisabledMetricsByChannelId = (id: number | null) => {
  if (id !== 1) {
    return WHISTLEBLOWING_SPECIFIC_METRICS;
  }
  return [];
};

function scaleNumber(
  number: number,
  screenWidth: number,
  maxNumber: number | null,
) {
  const scaleFactor = screenWidth / 1920;
  if (maxNumber && number * scaleFactor >= maxNumber) return maxNumber;
  return number * scaleFactor;
}

const ReportsPieChart = (props: ReportsPieChartProps) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const { report, ReportsDataList } = props;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const getColorPalette = (type: string) => {
    if (type === ReportFilters.CONFIDENTIALITY) return confidentialityPalette;
    return severityPalette;
  };

  const palette = getColorPalette(report);

  return (
    <Stack height='350px'>
      <PieChart
        series={[
          {
            data: ReportsDataList,
            highlightScope: { faded: 'global', highlighted: 'item' },
            arcLabel: (item) =>
              `${getPercentage(ReportsDataList, item.value)}%`,
            arcLabelMinAngle: 45,
            innerRadius: scaleNumber(40, windowWidth, 36),
            outerRadius: scaleNumber(165, windowWidth, 134),
            paddingAngle: 1,
            cornerRadius: 0,
            startAngle: 0,
            endAngle: 360,
            cx: scaleNumber(215, windowWidth, null),
            cy: 150,
          },
        ]}
        sx={{
          [`& .${pieArcLabelClasses.root}`]: {
            fontSize: '14px',
            fontWeight: 500,
            fill: '#FFFFFF',
          },
        }}
        colors={palette}
        slotProps={{
          legend: {
            hidden: true,
            labelStyle: {
              fontSize: 12,
            },
          },
        }}
      />
      <ReportGraphLegend
        legends={ReportsDataList.map((dataLabel) => dataLabel.label)}
        tooltipText={ReportsDataList.map(
          (dataFullLabel) => dataFullLabel.label,
        )}
        colors={palette}
        align='center'
      />
    </Stack>
  );
};

const ReportBreakdown = (props: PieChartProps) => {
  const intl = useIntl();
  const { filter, option } = props;
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const authSlice = useAuthSlice().auth;
  const isPartner =
    authSlice.user.type === ROLE_VALUES.PARTNER_USER ||
    authSlice.user.type === ROLE_VALUES.PARTNER_ADMIN;
  if (filter.filterBy === 'day') {
    // will change this later as this may affect other endpoints
    filter.filterBy = 'week';
  }
  const query = MetricsService.endpoints.pieChartMetrics.useQuery(
    {
      metricType: option,
      params: {
        filter_by: filter.filterBy,
        date: filter.date,
        date_end: filter.dateEnd,
        tenant_id:
          isPartner &&
          !(
            filter.tenantId === INITIAL_VALUE ||
            filter.tenantId === DEFAULT_VALUE
          )
            ? filter.tenantId
            : undefined,
        intake_channel: selectedChannel,
      },
    },
    {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
    },
  );

  return (
    <QueryResolver query={query}>
      {(data) => {
        if (data.every((reportData) => reportData.count === 0)) {
          return (
            <NoDataFound
              message={intl.formatMessage({ defaultMessage: 'No data found' })}
            />
          );
        } else {
          return (
            <ReportsPieChart
              ReportsDataList={data.map((item) => ({
                ...item,
                value: item.count,
              }))}
              report={option}
            />
          );
        }
      }}
    </QueryResolver>
  );
};

const ReportSeverity = () => {
  const intl = useIntl();
  const { selectedTenant } = usePartnerContext();
  const form = useDashoardFilter();

  const from = form.watch('date_range').from;
  const to = form.watch('date_range').to;

  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const [filterBySeverity, setFilterBySeverity] = useState<PieChartMetricTypes>(
    PieChartMetricTypes.SEVERITY,
  );
  const [bgColor, setBgColor] = useState('#E7E7E7');

  const intlFormatMessage = (value: string) => {
    switch (value) {
      case PieChartMetricTypes.SEVERITY:
        return intl.formatMessage({ defaultMessage: 'Severity' });
      case PieChartMetricTypes.CONFIDENTIALITY:
        return intl.formatMessage({ defaultMessage: 'Confidentiality' });
      case PieChartMetricTypes.AGE_OF_REPORT:
        return intl.formatMessage({ defaultMessage: 'Age of Report' });
      case PieChartMetricTypes.FINDINGS:
        return intl.formatMessage({ defaultMessage: 'Findings' });
      case PieChartMetricTypes.CHECK_BACK_RATIO:
        return intl.formatMessage({ defaultMessage: 'Check Back Ratio' });
      default:
        return '';
    }
  };

  return (
    <Stack
      component={Paper}
      sx={{
        height: '440px',
        border: '1px solid #00000016',
        borderRadius: 2,
        padding: '20px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography sx={headerText}>
          <FormattedMessage defaultMessage='Reports' />
        </Typography>
        {/* Filter */}
        <Select
          variant='standard'
          id='filter-date-type'
          IconComponent={(props) => <KeyboardArrowDownIcon {...props} />}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: 14,
              fontWeight: 500,
              fontFamily: 'Schibsted Grotesk',
              maxHeight: '40px',
              borderRadius: '6px',
              paddingTop: '8px',
              paddingLeft: '16px',
              paddingBottom: '8px',
              paddingRight: '16px',
              textAlign: 'center',
              backgroundColor: bgColor,
            },
            '& .MuiInputBase-input:hover': {
              backgroundColor: '#F6F6F6',
            },
            '.MuiSelect-icon': {
              color: '#181818',
              fontSize: 20,
            },
          }}
          value={filterBySeverity}
          disableUnderline
          renderValue={(selected) => intlFormatMessage(selected)}
          onOpen={() => setBgColor('#D1D1D1')}
          onClose={() => setBgColor('#E7E7E7')}
          onChange={(e) =>
            setFilterBySeverity(e.target.value as PieChartMetricTypes)
          }
        >
          {Object.values(PieChartMetricTypes).map((value) => {
            if (
              getDisabledMetricsByChannelId(selectedChannel).includes(value)
            ) {
              return null;
            }
            return (
              <MenuItem key={value} value={value}>
                <ListItemIcon>
                  {value === filterBySeverity ? <CheckIcon /> : ''}
                </ListItemIcon>
                <ListItemText>
                  <Typography
                    sx={{
                      fontFamily: 'Schibsted Grotesk',
                      fontSize: 14,
                      fontWeight: 500,
                    }}
                  >
                    {intlFormatMessage(value)}
                  </Typography>
                </ListItemText>
              </MenuItem>
            );
          })}
        </Select>
      </Box>
      <ReportBreakdown
        filter={{
          filterBy: 'day',
          date: from ? from.toISOString() : '',
          dateEnd: to ? to.toISOString() : '',
          tenantId: selectedTenant.id,
        }}
        option={filterBySeverity}
      />
    </Stack>
  );
};

export default ReportSeverity;
